import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  styled,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../api";
import {
  fetchGetAllUsuarios,
  fetchGetAllInmobiliarias,
  deleteUser,
} from "../../action";
import util from "../../utils/utilsFunctions";
import "../../css/styles.css";

const isMobile =
  !!navigator.userAgent.match(/iphone|android|blackberry/gi) || false;

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#00a62b",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const ListadoUsuarios = () => {
  const dispatch = useDispatch();

  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const itemsUsuarios = useSelector(
    (state) => state.app.itemsUsuarios || []
  );

  let userLogueado = null;
  try {
    userLogueado = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    userLogueado = null;
  }

  const cargarDatos = async () => {
    setIsLoading(true);
    dispatch(fetchGetAllInmobiliarias());
    await dispatch(fetchGetAllUsuarios());
    setIsLoading(false);
  };

  useEffect(() => {
    cargarDatos();
  }, [dispatch]);

  const showDeleteConfirmationDialog = async (e, row) => {
    e.stopPropagation();

    let alertMsg = "";
    if (row.tipoCuentaInt === 10) {
      try {
        Swal.showLoading();
        const [propsRes, usersRes] = await Promise.all([
          api.apiGetAllPropiedadesPaginadas("", row.iduser, 0, 999, "todos", "createdAtDescending"),
          api.apiGetUsuariosPaginados(99, 1, 1, row.iduser, false),
        ]);
        Swal.close();

        const ctdadPropiedades = propsRes && propsRes.results ? propsRes.results.length : 0;
        const ctdadUsuariosReferidos = usersRes && usersRes.results ? usersRes.results.length : 0;

        if (ctdadUsuariosReferidos > 0 || ctdadPropiedades > 0) {
          alertMsg = `Actualmente el broker posee ${ctdadUsuariosReferidos} usuarios referidos y ${ctdadPropiedades} propiedades vinculadas. `;
        } else {
          alertMsg = "Actualmente el broker no posee usuarios que haya referido ni propiedades vinculadas. ";
        }
      } catch (err) {
        console.error("Error al obtener datos del broker", err);
        Swal.close();
      }
    }

    Swal.fire({
      title: `Eliminar ${row.tipoCuentaInt === 10 ? "broker" : "usuario"} ${row.nombres}`,
      text: `${alertMsg}¿Confirma que desea eliminar este ${row.tipoCuentaInt === 10 ? "broker" : "usuario"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed || result.value) {
        Swal.showLoading();
        try {
          await dispatch(deleteUser(row.iduser));
          await dispatch(fetchGetAllUsuarios());
          Swal.fire({
            title: "Usuario eliminado",
            text: "Se ha eliminado el usuario exitosamente",
            icon: "success",
          });
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: "Ocurrió un error al eliminar el usuario",
            icon: "error",
          });
        }
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltro = (e) => {
    setFiltro(e.target.value);
    setPage(0);
  };

  const formattedItems = itemsUsuarios.map((item, i) => {
    return {
      id: item.id || i,
      nombres: `${item.nombres || ""} ${item.apellidos || ""}`.trim(),
      tipoCuenta: util.getTipoCuenta(item.tipoCuenta),
      tipoCuentaInt: item.tipoCuenta,
      email: item.email,
      iduser: item.id,
      editable:
        (moment(item.createdAt)
          .add(2, "days")
          .isBefore(moment()) &&
          (!item.registroCompletado || item.password != null)) ||
        item.registroCompletado,
    };
  });

  const filteredItems = formattedItems.filter((item) => {
    if (userLogueado && userLogueado.tipoCuenta === 10) {
      if (item.tipoCuenta !== "Usuario Web") return false;
    }
    if (!filtro) return true;
    const search = filtro.toLowerCase();
    const mailStr = typeof item.email === "string" ? item.email.toLowerCase() : "";
    const nameStr = typeof item.nombres === "string" ? item.nombres.toLowerCase() : "";
    const tipoStr = typeof item.tipoCuenta === "string" ? item.tipoCuenta.toLowerCase() : "";

    return (
      nameStr.includes(search) ||
      tipoStr.includes(search) ||
      mailStr.includes(search)
    );
  });

  const displayedItems =
    rowsPerPage > 0
      ? filteredItems.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      : filteredItems;

  const titleText =
    userLogueado && userLogueado.tipoCuenta === 6
      ? "Lista de Agentes"
      : "Lista de usuarios";

  const buttonText =
    userLogueado && userLogueado.tipoCuenta === 6
      ? "Crear Agente"
      : "Crear Usuario";

  return (
    <div className="contenido">
      <div className="header">
        <h2>{titleText}</h2>
      </div>

      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6} md={4} className="alignLeft">
          <Link to="/usuarios/agregar" style={{ textDecoration: "none" }}>
            <button className="boton">{buttonText}</button>
          </Link>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className="alignLeft">
          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="text"
              placeholder="Buscar"
              className="buscar"
              onChange={handleFiltro}
              value={filtro}
            />
            <SearchIcon style={{ position: "absolute", right: 10, top: 10, color: "#888" }} />
          </div>
        </Grid>
      </Grid>

      <div className="main">
        {isLoading && (
          <Grid container justifyContent="center" sx={{ my: 4 }}>
            <CircularProgress color="success" />
          </Grid>
        )}

        {!isLoading && (
          <Paper sx={{ width: "100%", overflow: "hidden", marginBottom: "2rem" }}>
            <TableContainer>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <StyledTableHead align="left">Nombre</StyledTableHead>
                    <StyledTableHead align="left">Tipo de Cuenta</StyledTableHead>
                    <StyledTableHead align="left">Email</StyledTableHead>
                    <StyledTableHead align="center">Acciones</StyledTableHead>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No se han encontrado resultados para su búsqueda
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedItems.map((row, idx) => (
                      <TableRow
                        key={row.id || idx}
                        className={idx % 2 === 0 ? "filaOscura" : ""}
                      >
                        <TableCell align="left">{row.nombres}</TableCell>
                        <TableCell align="left">{row.tipoCuenta}</TableCell>
                        <TableCell align="left">{row.email}</TableCell>
                        <TableCell align="center" style={{ minWidth: "100px" }}>
                          {row.editable ? (
                            <Tooltip title="Editar" aria-label="editar">
                              <Link to={`/usuarios/editar/${row.iduser}`}>
                                <IconButton color="primary" size="small">
                                  <EditIcon />
                                </IconButton>
                              </Link>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title="Este usuario ha sido referido hace menos de 48 horas, por lo que no puede ser editado todavía"
                              aria-label="ver detalles"
                            >
                              <span>
                                <IconButton disabled size="small">
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {(row.tipoCuentaInt === 10 || (userLogueado && userLogueado.tipoCuenta === 0)) && (
                            <Tooltip title="Eliminar" aria-label="eliminar">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={(e) => showDeleteConfirmationDialog(e, row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20]}
              SelectProps={{
                native: true,
              }}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        )}
      </div>
    </div>
  );
};

export default ListadoUsuarios;
